import { useCallback } from 'react';

import {
  Cma_Report_Source_Enum_Enum,
  type LeadSelectFragment,
  type Leads_Bool_Exp,
  Order_By,
} from '@graphql/__generated__/graphql';
import {
  type FormDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';

type CreateCMAFromLeadFormData = {
  lead: LeadSelectFragment;
};

type CreateCMAFromLeadProps = {
  onClose: () => void;
  handleCreateCMA: (
    leadId: string,
    contactId?: string,
  ) => Promise<string | undefined>;
};

const CreateCMAFromLead = ({
  onClose,
  handleCreateCMA,
}: CreateCMAFromLeadProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { track } = useAnalytics();

  const fromLeadFormDefinition: FormDefinitionType<CreateCMAFromLeadFormData> =
    useCallback(
      ({ t }) => [
        {
          label: t('Lead'),
          type: 'lead',
          name: 'lead',
          placeholder: t('Search lead by user or address'),
          instantQuery: true,
          autoFocus: true,
          customWhereClause: (searchValue: string = '') => {
            const trimmedSearch = searchValue.replace(/\s+/g, ' ').trim();
            const searchCondition: Leads_Bool_Exp = {
              _or: [
                { contact: { full_name: { _ilike: `%${trimmedSearch}%` } } },
                { property: { route: { _ilike: `%${trimmedSearch}%` } } },
                { property: { locality: { _ilike: `%${trimmedSearch}%` } } },
              ],
            };

            const whereClause: Leads_Bool_Exp = {
              _and: [],
            };

            if (trimmedSearch !== '') {
              whereClause._and?.push(searchCondition);
            }

            if (me?.is_admin) {
              return whereClause;
            }

            // Only include claimed leads
            whereClause._and?.push({
              lead_agents: {
                claimed_at: { _is_null: false },
                passed_at: { _is_null: true },
              },
            });

            return whereClause;
          },
          orderBy: { requalified_or_created_at: Order_By.Desc },
        },
      ],
      [me],
    );

  const handleSubmit = useCallback(
    async (formData: CreateCMAFromLeadFormData) => {
      const startTime = Date.now();

      const cmaReportId = await handleCreateCMA(
        formData.lead.id,
        formData.lead.contact?.id,
      );

      if (cmaReportId) {
        const baseProperties = getBaseCmaEventProperties(me, cmaReportId);

        if (baseProperties) {
          track(ANALYTICS_EVENTS.CMA_CREATED, {
            ...baseProperties,
            source: Cma_Report_Source_Enum_Enum.CmaFromLead,
            lead_id: formData.lead.id,
            time_to_create_ms: Date.now() - startTime,
          });
        }
      }
    },
    [track, handleCreateCMA, me],
  );

  return (
    <RaForm
      onCancel={onClose}
      formDefinition={fromLeadFormDefinition}
      onSubmit={handleSubmit}
      submitButtonText={t('Create')}
    />
  );
};

export default CreateCMAFromLead;
